/**
 * Trial terms and seat limits — the FRONTEND MIRROR of the backend SSOT.
 *
 * WHY THIS EXISTS (2026-09-09). The frontend had no import of these constants
 * at all, so the trial got described three different ways on three surfaces
 * and none of them could be checked:
 *   /pricing         "over 100 jobs on us"
 *   /subscription    a pr0 balance, with the dollar figure nowhere in sight
 *   /onboarding      "trial credit: $200"
 * Each was hand-typed. This module is the one place a page reads them from,
 * on the same mirror-plus-gate pattern that
 * src/constants/permissionCatalog.data.json already uses:
 * scripts/check-trial-terms-sync.mjs fails the build if these drift from
 * functions/src/accessCodeRedemption.ts or functions/src/rbac/seatLimit.ts.
 *
 * ⚠️ THE LIVE VALUES CAN BE HIGHER. A Firestore doc (ops/trialTiers) may
 * override the tier terms without a deploy, and it may only ever WIDEN them.
 * So treat these as the published floor: safe to promise, never the ceiling.
 * Anything that must reflect a specific account's real standing reads the
 * account, not this file.
 */

/** One tier's terms: what the wallet is credited and how long the clock runs. */
export interface TrialTierTerms {
  creditAmountCents: number;
  durationDays: number;
}

/**
 * Mirrors DEFAULT_TRIAL_TIERS in functions/src/accessCodeRedemption.ts.
 *   a       PREST0-… invitation codes
 *   b       EVAL-… invitation codes
 *   organic no code at all — the codeless signup every visitor gets
 */
export const TRIAL_TIERS = {
  a: { creditAmountCents: 100000, durationDays: 28 },
  b: { creditAmountCents: 30000, durationDays: 14 },
  organic: { creditAmountCents: 20000, durationDays: 14 },
} as const satisfies Record<'a' | 'b' | 'organic', TrialTierTerms>;

/** What someone who just signs up gets, with no code and no card. */
export const ORGANIC_TRIAL = TRIAL_TIERS.organic;

/** Whole dollars, for copy. $200 today. */
export const ORGANIC_TRIAL_CREDIT_USD = ORGANIC_TRIAL.creditAmountCents / 100;

/** Days on the clock. 14 today. */
export const ORGANIC_TRIAL_DAYS = ORGANIC_TRIAL.durationDays;

/**
 * THE TRIAL CTA — why "over 100 jobs on us" is accurate, and what would break it.
 *
 * The organic trial loads $200 of credit. Divided by the published Standard
 * Rate ($2.18/pr0) that looks like ~92 pr0, which would make "over 100 jobs"
 * an overstatement. That reading is wrong: the rate card is not what the
 * wallet charges.
 *
 * PR0-WORK-UNIT-ECONOMICS-20260819.md:178 — "Billing today is
 * consumption-derived: the meter charges raw x 5 / 2.18 and the rate card is
 * published guidance that nothing enforces", and :286 — "the meter charges
 * what actually ran". A task turn's MEASURED cost is p50 0.13 pr0 (all-time
 * p90 0.97), so $200 buys several hundred ordinary jobs, not ninety-two. The
 * claim is conservative, and the owner confirmed it on 2026-09-09.
 *
 * ⚠️ IT INVERTS if the flat rate card ever becomes the actual charge — Phase
 * 2/5 of the work-unit migration ("server-stamped flat-rate wu_amount"),
 * unshipped as of 2026-09-09. At flat rates the trial would buy at most ~92
 * jobs and every "over 100 jobs" string below must change with it.
 *
 * Call sites: LandingPage.tsx (hero + closing CTA), Pricing.tsx (hire card),
 * WorkUnitCalculator.tsx (modal CTA).
 */
export const TRIAL_CTA_JOBS_CLAIM_DEPENDS_ON_CONSUMPTION_BILLING = true;

/** Seats an organization may ADD during the trial promo, beyond the owner. */
export const TRIAL_ADDED_SEATS = 5;

/** Owner seat plus the trial invitees — the total, counting pending invites. */
export const TRIAL_MAX_TEAM_SIZE = 1 + TRIAL_ADDED_SEATS;

/** Days of grace after a payment fails before AI access stops. */
export const PAID_LAPSE_GRACE_DAYS = 14;

/**
 * Formats a tier's credit for copy: "$200", never "$200.00".
 * Trial figures are always whole dollars today; the rounding is here so a
 * future odd amount degrades to something readable rather than to cents.
 */
export function formatTrialCreditUsd(terms: TrialTierTerms = ORGANIC_TRIAL): string {
  const usd = terms.creditAmountCents / 100;
  return `$${Number.isInteger(usd) ? usd.toLocaleString('en-US') : usd.toFixed(2)}`;
}
